/**
 * One import, as it runs and after: live counts, Cancel, Resume and, after a
 * sample, "Import the rest".
 *
 * The counts are the import worker's own tallies, polled while the job runs.
 * They are never worked out from the rows themselves, which would be a scan of
 * the very table a running import is writing to.
 */
import { useState } from 'react';
import { Alert, Button, Group, Modal, Paper, Progress, SimpleGrid, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCancelImport, useImportProgress, useImportRest, useResumeImport } from '../api/queries';
import { formatNumber } from '../theme';
import { useVocab } from '../vocab';

/** "About 3 hours". Rounded on purpose: this is an estimate, not a timer. */
export function formatDuration(seconds: number): string {
  if (seconds < 1.5) return 'a second';
  if (seconds < 90) return `${Math.round(seconds)} seconds`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${Math.round(minutes)} minutes`;
  const hours = minutes / 60;
  if (hours < 36) {
    const whole = Math.round(hours * 2) / 2;
    return `${whole} ${whole === 1 ? 'hour' : 'hours'}`;
  }
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** The status a person reads, in the app's words. */
export function importStatusLabel(status: string, cancelling: boolean): string {
  if (cancelling) return 'Cancelling';
  switch (status) {
    case 'pending': return 'Waiting to start';
    case 'processing': return 'Running';
    case 'paused': return 'Stopped';
    case 'completed': return 'Finished';
    case 'cancelled': return 'Cancelled';
    case 'failed': return 'Failed';
    default: return status;
  }
}

export function importStatusColour(status: string): string {
  switch (status) {
    case 'completed': return 'chartGreen';
    case 'failed': return 'chartRed';
    case 'paused': return 'chartRed';
    case 'cancelled': return 'gray';
    default: return 'chartAmber';
  }
}

function Tally({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={600} c={value > 0 ? tone : undefined}>{formatNumber(value)}</Text>
    </div>
  );
}

export function ImportJobCard({ jobId, title }: { jobId: string; title?: string }) {
  const v = useVocab();
  const progress = useImportProgress(jobId);
  const cancel = useCancelImport();
  const resume = useResumeImport();
  const importRest = useImportRest();
  const [confirming, setConfirming] = useState(false);
  /* "Import the rest" starts a new job, and this card follows it. */
  const [followingId, setFollowingId] = useState<string | null>(null);

  const job = progress.data;
  if (followingId && followingId !== jobId) {
    return <ImportJobCard jobId={followingId} title={title} />;
  }
  if (!job) return null;

  const live = job.live;
  const cancelling = live?.step === 'cancelling';
  const running = job.status === 'pending' || job.status === 'processing';
  const processed = live?.rows_processed ?? 0;
  const total = live?.rows_total || job.rows_total || 0;
  const sample = live?.sample;
  const failed = (message: string) => (e: unknown) =>
    notifications.show({ color: 'red', title: message, message: (e as Error).message });

  return (
    <Paper withBorder p="sm" radius="md" data-testid="import-job-card">
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <div>
            <Text size="sm" fw={600}>{title ?? (job.file_name || 'Import')}</Text>
            <Text size="xs" c={importStatusColour(job.status)} fw={600}>
              {importStatusLabel(job.status, cancelling)}
              {job.campaign_name ? ` · ${job.campaign_name}` : ''}
            </Text>
          </div>
          <Group gap="xs" wrap="nowrap">
            {job.status === 'paused' && job.can_resume && (
              <Button
                size="xs"
                loading={resume.isPending}
                onClick={() => resume.mutate(jobId, { onError: failed('Could not resume') })}
              >
                Resume
              </Button>
            )}
            {(running || job.status === 'paused') && (
              <Button
                size="xs"
                variant="light"
                color="red"
                disabled={cancelling}
                onClick={() => setConfirming(true)}
              >
                {cancelling ? 'Cancelling' : 'Cancel'}
              </Button>
            )}
          </Group>
        </Group>

        {job.stop_reason && job.status === 'paused' && (
          <Alert color="chartRed" variant="light" title="Stopped so nothing more is wasted">
            <Text size="sm">{job.stop_reason}</Text>
            <Text size="xs" c="dimmed" mt={4}>
              {job.can_resume
                ? `Fix the ${v.one('route')}, then Resume. It carries on from the next row, and no row already sent goes again.`
                : 'This import had phone checks on, so it cannot be resumed without paying for them again.'}
            </Text>
          </Alert>
        )}
        {job.error_message && <Text size="xs" c="chartRed">{job.error_message}</Text>}

        {total > 0 && (
          <div>
            <Progress
              value={live?.progress ?? 0}
              animated={running && !cancelling}
              color={importStatusColour(job.status)}
              aria-label="Import progress"
            />
            <Group justify="space-between" mt={4}>
              <Text size="xs" c="dimmed">
                {formatNumber(processed)} of {formatNumber(total)} rows
                {live?.rows_skipped ? `, ${formatNumber(live.rows_skipped)} malformed` : ''}
              </Text>
              {running && live?.rate_per_sec != null && live.rate_per_sec > 0 && (
                <Text size="xs" c="dimmed">
                  {formatNumber(Math.round(live.rate_per_sec))} rows a second
                  {live.eta_seconds != null && <> · about {formatDuration(live.eta_seconds)} left</>}
                </Text>
              )}
            </Group>
          </div>
        )}

        {live && live.distribute ? (
          <SimpleGrid cols={{ base: 3, sm: 6 }} spacing="xs">
            <Tally label="Accepted" value={live.accepted} tone="chartGreen" />
            <Tally label="Rejected" value={live.rejected} tone="chartRed" />
            <Tally label="Filtered" value={live.filtered} />
            <Tally label="Duplicate" value={live.duplicate} />
            <Tally label="Suppressed" value={live.suppressed} />
            <Tally label="Capped" value={live.capped} />
          </SimpleGrid>
        ) : live ? (
          <Text size="xs" c="dimmed">{formatNumber(live.rows_imported)} imported</Text>
        ) : null}

        {live && live.lost > 0 && (
          <Alert color="chartRed" variant="light">
            <Text size="sm">
              {formatNumber(live.lost)} rows could not be processed or stored, even after retrying.
            </Text>
          </Alert>
        )}

        {live?.top_rejection && (
          <Text size="xs" c="dimmed">
            Most common rejection ({formatNumber(live.top_rejection.count)}):{' '}
            {live.top_rejection.reason}
          </Text>
        )}

        {job.status === 'cancelled' && (
          <Text size="xs" c="dimmed">
            Rows already imported stay. Nothing further was sent.
          </Text>
        )}

        {sample && job.status === 'completed' && (
          <Stack gap={6}>
            <Text size="sm">
              Sent the first {formatNumber(sample.limit)} rows: {formatNumber(live?.accepted ?? 0)} accepted,{' '}
              {formatNumber(live?.rejected ?? 0)} rejected.
            </Text>
            {sample.rest_available && (
              <Group>
                <Button
                  size="xs"
                  loading={importRest.isPending}
                  onClick={() =>
                    importRest.mutate(jobId, {
                      onSuccess: (r) => setFollowingId(r.job_id),
                      onError: failed('Could not import the rest'),
                    })
                  }
                >
                  Import the rest
                  {sample.rows_left != null ? ` (${formatNumber(sample.rows_left)} rows)` : ''}
                </Button>
                <Text size="xs" c="dimmed">The rows already sent are skipped.</Text>
              </Group>
            )}
          </Stack>
        )}
      </Stack>

      <Modal opened={confirming} onClose={() => setConfirming(false)} title="Cancel this import?" size="sm">
        <Stack>
          <Text size="sm">
            {`No more rows will be sent or imported. Rows already imported stay, and ${v.many('buyer')} keep what they have received. Posts on their way when you cancel will finish.`}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirming(false)}>Keep going</Button>
            <Button
              color="red"
              loading={cancel.isPending}
              onClick={() =>
                cancel.mutate(jobId, {
                  onSuccess: () => setConfirming(false),
                  onError: failed('Could not cancel'),
                })
              }
            >
              Cancel import
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
